import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { BasicButton, ButtonType, Dropdown, Icon, IconSize } from "@gaming/xds-react-js";
import { Add, Attach, Close, Download, Game, MoreActions, Send } from "@gaming/xds-react-js-icons";

import { CaseMatcherDebugPanel } from "../../components/CaseMatcherDebugPanel";
import { FeatureFlags, useFeatureFlag } from "../../featureFlags";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { getSupportSubject } from "../../constants/supportSubjects";
import { xboxOnboardSiteLocationPrefix } from "../../constants/urls";
import { useRequireAuth } from "../../constants/urlsClientSide";
import {
  fetchSupportJson,
  formatFileSize,
  formatSupportDate,
  getSupportAttachmentUrl,
  getSupportSeverityStringKey,
  isSupportCaseClosed,
  markSupportCaseViewed,
  supportPagePath,
  SupportRequestError,
  toSupportReplyMailtoHref,
  uploadSupportAttachment,
} from "../../helpers/supportCases";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import {
  previewableImageTypes,
  SupportAttachment,
  supportAttachmentMaxBytes,
  SupportCaseDetails,
  SupportCaseMessage,
  SupportCaseSeverity,
  SupportCaseStatus,
  SupportCaseThread,
} from "../../models/supportCases";
import { getAvatarClass, getMemberInitials } from "../AdminPage/avatarHelpers";
import { SupportNewCaseLocationState } from "../PartnerSupportNewPage";
import * as styles from "./PartnerSupportCasePage.module.css";

const formatTimestamp = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

type ThreadState = Pick<SupportCaseThread, "messages" | "page" | "totalPages" | "replyTo">;

export const PartnerSupportCasePage: React.FC = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.PartnerSettings);
  const isSignedIn = useRequireAuth();
  const isEmailReplyEnabled = useFeatureFlag(FeatureFlags.EnableHubCaseEmailReply);
  const { caseId } = useParams<{ caseId: string }>();
  const location = useLocation<SupportNewCaseLocationState | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewDialog = useRef<HTMLDialogElement>(null);
  const closeDialog = useRef<HTMLDialogElement>(null);
  const menuTrigger = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<SupportAttachment>();
  const [details, setDetails] = useState<SupportCaseDetails>();
  const [thread, setThread] = useState<ThreadState>({ messages: [], page: 0, totalPages: 0 });
  const [errorStatus, setErrorStatus] = useState<number>();
  const [isLoadingEarlier, setIsLoadingEarlier] = useState(false);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState<ReadonlyArray<File>>([]);
  const [isSending, setIsSending] = useState(false);
  const [notice, setNotice] = useState(location.state?.attachmentFailed ? "attachmentFailed" : "");

  const [
    supportLabel,
    requestBreadcrumb,
    supportAgent,
    typeMessage,
    send,
    sending,
    addAttachment,
    removeFile,
    detailsTitle,
    gameRelated,
    tagsLabel,
    severityTag,
    lastUpdate,
    loadEarlier,
    replyError,
    caseClosedNotice,
    notFound,
    accessDenied,
    loadError,
    attachmentError,
    fileTooLarge,
    inProgress,
    resolved,
    closed,
    download,
    closeLabel,
    replyByEmail,
    submittedByLabel,
    submittedByYou,
    submittedLabel,
    moreOptions,
    closeRequest,
    closeRequestConfirm,
    closeError,
    cancel,
  ] = useStrings(
    "hubSupportRequestsTitle",
    "hubSupportRequestBreadcrumb",
    "hubSupportSupportAgent",
    "hubSupportTypeMessage",
    "hubSupportSend",
    "ticketSendingLabel",
    "ticketAddAttachmentAriaLabel",
    "ticketRemoveItemAriaLabel",
    "hubSupportDetails",
    "hubSupportGameRelated",
    "ticketTagsLabel",
    "hubSupportSeverityTag",
    "hubSupportLastUpdate",
    "hubSupportLoadEarlier",
    "hubSupportReplyError",
    "hubSupportCaseClosedNotice",
    "hubSupportNotFound",
    "ticketAccessDeniedMsg",
    "hubSupportLoadError",
    "hubSupportAttachmentError",
    "hubSupportFileTooLarge",
    "inProgress",
    "ticketResolvedLabel",
    "ticketClosedLabel",
    "devGuideDownloadLink",
    "closeAriaLabel",
    "hubSupportReplyByEmail",
    "hubSupportSubmittedBy",
    "hubSupportSubmittedByYou",
    "submitted",
    "moreOptionsAriaLabel",
    "hubSupportCloseRequest",
    "hubSupportCloseRequestConfirm",
    "hubSupportCloseError",
    "cancel"
  );
  const supportCase = details?.supportCase;
  const supportEmail = useStrings("partnerSettingsSupportEmail")[0];
  // ponytail: no support email yet = no tracking token, so CRM may not thread this reply onto the case.
  const emailReplyTo = isEmailReplyEnabled
    ? thread.replyTo ??
      (supportCase && {
        address: supportEmail,
        subject: `Re: ${supportCase.title} [${supportCase.ticketNumber}]`,
      })
    : undefined;
  const subject = getSupportSubject(supportCase?.subjectId);
  const [categoryLabel, severityLabel] = useStrings(
    subject?.stringKey ?? "other",
    getSupportSeverityStringKey(supportCase?.severity ?? SupportCaseSeverity.Medium)
  );

  const casePath = `/cases/${encodeURIComponent(caseId)}`;
  useEffect(() => {
    if (!isPageReady || !isSignedIn) {
      return;
    }
    let isCurrent = true;
    Promise.all([
      fetchSupportJson<SupportCaseDetails>(casePath),
      fetchSupportJson<SupportCaseThread>(`${casePath}/messages`),
    ])
      .then(([caseDetails, caseThread]) => {
        if (isCurrent) {
          setDetails(caseDetails);
          setThread(caseThread);
          markSupportCaseViewed(caseId);
        }
      })
      .catch((error: unknown) => {
        if (isCurrent) {
          setErrorStatus(error instanceof SupportRequestError ? error.status : 0);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [caseId, casePath, isPageReady, isSignedIn]);

  const isLive = !!supportCase && !isSupportCaseClosed(supportCase);
  const newestSentOn = thread.messages[thread.messages.length - 1]?.sentOn;
  useEffect(() => {
    if (!isLive) {
      return;
    }
    // ponytail: 30s visible-tab poll + refetch on tab return; swap for push if CRM ever offers it.
    const refresh = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const since = newestSentOn ? `?since=${encodeURIComponent(newestSentOn)}` : "";
      fetchSupportJson<SupportCaseThread>(`${casePath}/messages${since}`)
        .then(({ messages }) =>
          setThread(current => {
            const known = new Set(current.messages.map(message => message.messageId));
            const added = messages.filter(message => !known.has(message.messageId));
            return added.length
              ? { ...current, messages: [...current.messages, ...added] }
              : current;
          })
        )
        .catch(() => undefined);
    };
    const timer = window.setInterval(refresh, 30_000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [casePath, isLive, newestSentOn]);

  const onLoadEarlier = async () => {
    setIsLoadingEarlier(true);
    try {
      const earlier = await fetchSupportJson<SupportCaseThread>(
        `${casePath}/messages?page=${thread.page + 1}`
      );
      setThread(current => ({
        ...current,
        messages: [...earlier.messages, ...current.messages],
        page: earlier.page,
        totalPages: earlier.totalPages,
      }));
    } catch {
      setNotice("loadError");
    } finally {
      setIsLoadingEarlier(false);
    }
  };

  const addFiles = (fileList: FileList | null) => {
    const selected = Array.from(fileList ?? []);
    const tooLarge = selected.find(file => file.size > supportAttachmentMaxBytes);
    setNotice(tooLarge ? fileTooLarge.replace("{0}", tooLarge.name) : "");
    setFiles(current => [
      ...current,
      ...selected.filter(file => file.size <= supportAttachmentMaxBytes),
    ]);
  };

  const onSend = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!reply.trim() || isSending) {
      return;
    }
    if (emailReplyTo) {
      window.location.href = toSupportReplyMailtoHref(emailReplyTo, reply.trim());
      setReply("");
      return;
    }
    setIsSending(true);
    setNotice("");
    try {
      const { messageId } = await fetchSupportJson<{ messageId: string }>(`${casePath}/messages`, {
        method: "POST",
        body: JSON.stringify({ body: reply.trim() }),
      });
      const uploads = await Promise.allSettled(
        files.map(file => uploadSupportAttachment(caseId, file, messageId))
      );
      setReply("");
      setFiles([]);
      if (uploads.some(result => result.status === "rejected")) {
        setNotice("attachmentFailed");
      }
      markSupportCaseViewed(caseId);
      setThread(await fetchSupportJson<SupportCaseThread>(`${casePath}/messages`));
    } catch {
      setNotice("replyError");
    } finally {
      setIsSending(false);
    }
  };

  const onCloseCase = async () => {
    setIsClosing(true);
    setNotice("");
    try {
      await fetchSupportJson(`${casePath}/close`, { method: "POST" });
      setDetails(await fetchSupportJson<SupportCaseDetails>(casePath));
    } catch {
      setNotice("closeError");
    } finally {
      setIsClosing(false);
      closeDialog.current?.close();
    }
  };

  const noticeTexts: Record<string, string> = {
    attachmentFailed: attachmentError,
    replyError,
    loadError,
    closeError,
  };
  const noticeText = noticeTexts[notice] ?? notice;

  const statusLabel =
    supportCase?.status === SupportCaseStatus.Resolved
      ? resolved
      : supportCase?.status === SupportCaseStatus.Closed
      ? closed
      : inProgress;

  const openPreview = (attachment: SupportAttachment) => {
    setPreviewAttachment(attachment);
    previewDialog.current?.showModal();
  };

  // Images show as thumbnails that open in a preview; other files download.
  const renderMessage = ({
    messageId,
    fromXbox,
    senderName,
    sentOn,
    body,
    attachments = [],
  }: Omit<SupportCaseMessage, "attachmentCount">) => (
    <li
      key={messageId}
      className={`${styles.message} ${fromXbox ? styles.support : styles.partner}`}
      data-testid={fromXbox ? "support-message-agent" : "support-message-partner"}
    >
      <span className={styles.messageMeta}>
        {fromXbox ? supportAgent : senderName} · {formatTimestamp(sentOn)}
      </span>
      {body && <p className={styles.bubble}>{body}</p>}
      {attachments.length > 0 && (
        <ul className={styles.attachments}>
          {attachments.map(attachment => (
            <li key={attachment.attachmentId}>
              {previewableImageTypes.has(attachment.mimeType) ? (
                <button
                  type="button"
                  className={styles.thumbnail}
                  onClick={() => openPreview(attachment)}
                >
                  <img
                    src={getSupportAttachmentUrl(caseId, attachment.attachmentId)}
                    alt={attachment.name}
                    loading="lazy"
                  />
                </button>
              ) : (
                <a
                  className={styles.attachment}
                  href={getSupportAttachmentUrl(caseId, attachment.attachmentId)}
                  download={attachment.name}
                >
                  <Icon aria-hidden="true" icon={Attach} iconSize={IconSize.Custom} />
                  <span className={styles.attachmentName}>{attachment.name}</span>
                  <span className={styles.muted}>{formatFileSize(attachment.size)}</span>
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );

  const breadcrumbSteps = [{ label: supportLabel, to: supportPagePath }];
  const breadcrumbLabel = requestBreadcrumb.replace("{0}", supportCase?.title ?? "");

  if (errorStatus !== undefined) {
    return (
      <PartnerPageLayout breadcrumbLabel={breadcrumbLabel} breadcrumbSteps={breadcrumbSteps}>
        <p role="alert" className={styles.pageMessage}>
          {errorStatus === 404 ? notFound : errorStatus === 403 ? accessDenied : loadError}
        </p>
      </PartnerPageLayout>
    );
  }

  if (!isPageReady || !isSignedIn || !details || !supportCase) {
    return (
      <PartnerPageLayout breadcrumbLabel={breadcrumbLabel} breadcrumbSteps={breadcrumbSteps}>
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }

  const isClosed = isSupportCaseClosed(supportCase);
  const collapseWhitespace = (value = "") => value.replace(/\s+/g, " ").trim();
  // Email-originated cases copy the first email into the description; show it once.
  const isDescriptionFirstMessage =
    !!thread.messages[0] &&
    collapseWhitespace(details.description) === collapseWhitespace(thread.messages[0].body);

  return (
    <PartnerPageLayout breadcrumbLabel={breadcrumbLabel} breadcrumbSteps={breadcrumbSteps} wide>
      <div className={styles.columns}>
        <section className={styles.main} aria-labelledby="support-case-title">
          <header className={styles.caseHeader}>
            <span className={`${styles.status} ${isClosed ? styles.closed : ""}`}>
              {statusLabel}
            </span>
            {details.crmUrl ? (
              <a
                className={styles.muted}
                href={details.crmUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {supportCase.ticketNumber}
              </a>
            ) : (
              <span className={styles.muted}>{supportCase.ticketNumber}</span>
            )}
            {!isClosed && (
              <div className={styles.menuButton}>
                <button
                  ref={menuTrigger}
                  type="button"
                  className={styles.iconButton}
                  aria-label={moreOptions}
                  aria-haspopup="menu"
                  aria-expanded={isMenuOpen}
                  onClick={() => setIsMenuOpen(open => !open)}
                >
                  <Icon aria-hidden="true" icon={MoreActions} iconSize={IconSize.Custom} />
                </button>
                {isMenuOpen && (
                  <Dropdown
                    isOpen
                    onClose={() => setIsMenuOpen(false)}
                    relativeRef={menuTrigger as React.RefObject<HTMLElement>}
                    horizontalAlignment="right"
                    backgroundDismissable
                    dimBackground="none"
                    className={styles.menu}
                    zIndex={1000}
                  >
                    <div role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.menuItem}
                        onClick={() => {
                          setIsMenuOpen(false);
                          closeDialog.current?.showModal();
                        }}
                      >
                        {closeRequest}
                      </button>
                    </div>
                  </Dropdown>
                )}
              </div>
            )}
            <h1 id="support-case-title">{supportCase.title}</h1>
          </header>
          <div className={styles.thread}>
            {thread.page < thread.totalPages && (
              <button
                type="button"
                className={styles.loadEarlier}
                onClick={onLoadEarlier}
                disabled={isLoadingEarlier}
              >
                {loadEarlier}
              </button>
            )}
            <ol className={styles.messages}>
              {thread.page >= thread.totalPages &&
                !isDescriptionFirstMessage &&
                renderMessage({
                  messageId: "description",
                  fromXbox: false,
                  senderName: supportCase.createdByName,
                  sentOn: supportCase.createdOn,
                  body: details.description,
                  attachments: details.attachments,
                })}
              {thread.messages.map(renderMessage)}
            </ol>
          </div>
          {noticeText && (
            <p role="alert" className={styles.notice}>
              {noticeText}
            </p>
          )}
          {isClosed ? (
            <p className={styles.closedNotice}>{caseClosedNotice}</p>
          ) : (
            <form className={styles.composer} onSubmit={onSend}>
              {files.length > 0 && (
                <ul className={styles.attachments}>
                  {files.map((file, index) => (
                    <li key={`${file.name}-${index}`} className={styles.attachment}>
                      <Icon aria-hidden="true" icon={Attach} iconSize={IconSize.Custom} />
                      <span className={styles.attachmentName}>{file.name}</span>
                      <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={removeFile.replace("{0}", file.name)}
                        onClick={() => setFiles(current => current.filter((_, i) => i !== index))}
                      >
                        <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className={styles.composerRow}>
                {!emailReplyTo && (
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={addAttachment}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Icon aria-hidden="true" icon={Add} iconSize={IconSize.Custom} />
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={event => {
                    addFiles(event.target.files);
                    event.target.value = "";
                  }}
                />
                <textarea
                  rows={1}
                  value={reply}
                  placeholder={typeMessage}
                  aria-label={typeMessage}
                  onChange={event => setReply(event.target.value)}
                />
                <button
                  type="submit"
                  className={styles.sendButton}
                  aria-label={emailReplyTo ? replyByEmail : isSending ? sending : send}
                  disabled={!reply.trim() || isSending}
                >
                  <Icon aria-hidden="true" icon={Send} iconSize={IconSize.Custom} />
                </button>
              </div>
            </form>
          )}
        </section>
        <aside className={styles.details} aria-labelledby="support-details-title">
          <h2 id="support-details-title">{detailsTitle}</h2>
          {details.submittedBy && (
            <div className={styles.detailGroup}>
              <h3>{submittedByLabel}</h3>
              <div className={styles.game}>
                <span
                  className={`${styles.avatar} ${getAvatarClass(details.submittedBy.contactId)}`}
                  aria-hidden="true"
                >
                  {getMemberInitials(details.submittedBy)}
                </span>
                <span>
                  <span className={styles.gameName}>
                    {supportCase.isOwn
                      ? submittedByYou.replace("{0}", supportCase.createdByName)
                      : supportCase.createdByName}
                  </span>
                  <span className={styles.muted}>{details.submittedBy.email}</span>
                </span>
              </div>
            </div>
          )}
          {supportCase.gameName && (
            <div className={styles.detailGroup}>
              <h3>{gameRelated}</h3>
              <div className={styles.game}>
                <span className={styles.gameIcon}>
                  <Icon aria-hidden="true" icon={Game} iconSize={IconSize.Custom} />
                </span>
                <span>
                  <Link
                    className={styles.gameName}
                    to={`${xboxOnboardSiteLocationPrefix}/game-concept/${supportCase.gameConceptId}`}
                  >
                    {supportCase.gameName}
                  </Link>
                </span>
              </div>
            </div>
          )}
          {(supportCase.severity || subject) && (
            <div className={styles.detailGroup}>
              <h3>{tagsLabel}</h3>
              <div className={styles.tags}>
                {supportCase.severity && (
                  <span className={`${styles.tag} ${styles[supportCase.severity]}`}>
                    {severityTag.replace("{0}", severityLabel)}
                  </span>
                )}
                {subject && <span className={styles.tag}>{categoryLabel}</span>}
              </div>
            </div>
          )}
          <div className={styles.detailGroup}>
            <h3>{submittedLabel}</h3>
            <span>{formatSupportDate(supportCase.createdOn)}</span>
          </div>
          <div className={styles.detailGroup}>
            <h3>{lastUpdate}</h3>
            <span>{formatSupportDate(supportCase.modifiedOn)}</span>
          </div>
        </aside>
      </div>
      <CaseMatcherDebugPanel caseId={caseId} />
      <dialog
        ref={closeDialog}
        className={styles.confirmDialog}
        aria-labelledby="support-close-title"
        aria-busy={isClosing}
      >
        <form method="dialog">
          <header className={styles.confirmHeader}>
            <h2 id="support-close-title">{closeRequest}</h2>
            <button
              type="submit"
              className={styles.iconButton}
              aria-label={closeLabel}
              disabled={isClosing}
            >
              <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
            </button>
          </header>
          <p>{closeRequestConfirm}</p>
          <div className={styles.confirmActions}>
            <BasicButton
              as="button"
              buttonType={ButtonType.Secondary}
              type="submit"
              disabled={isClosing}
            >
              {cancel}
            </BasicButton>
            <BasicButton
              as="button"
              buttonType={ButtonType.Brand}
              type="button"
              disabled={isClosing}
              onClick={onCloseCase}
            >
              {closeRequest}
            </BasicButton>
          </div>
        </form>
      </dialog>
      <dialog
        ref={previewDialog}
        className={styles.previewDialog}
        aria-label={previewAttachment?.name}
        onClose={() => setPreviewAttachment(undefined)}
      >
        {previewAttachment && (
          <>
            <header className={styles.previewHeader}>
              <span className={styles.attachmentName}>{previewAttachment.name}</span>
              <a
                className={styles.previewAction}
                href={getSupportAttachmentUrl(caseId, previewAttachment.attachmentId)}
                download={previewAttachment.name}
              >
                <Icon aria-hidden="true" icon={Download} iconSize={IconSize.Custom} />
                {download}
              </a>
              <form method="dialog">
                <button type="submit" className={styles.iconButton} aria-label={closeLabel}>
                  <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
                </button>
              </form>
            </header>
            <img
              src={getSupportAttachmentUrl(caseId, previewAttachment.attachmentId)}
              alt={previewAttachment.name}
            />
          </>
        )}
      </dialog>
    </PartnerPageLayout>
  );
};
